import React from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  DialogContent,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import UploadIcon from '@mui/icons-material/Upload';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { ImportMode } from 'api/consumablesApi';
import {
  DEFAULT_RATES_TEMPLATE_FILENAME,
  DEFAULT_RATES_DESCRIPTION_TEXT,
  DEFAULT_RATES_INFO_TEXT,
} from './ConsumableImportText';

interface Props {
  file: File | null;
  setFile: (file: File | null) => void;
  isLoading: boolean;
  mode: ImportMode;
  noExistingData: boolean;
  onDownloadTemplate: () => void;
  isDownloadingTemplate: boolean;
}

const getFileName = (name: string) => {
  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const ext = dotIndex > 0 ? name.slice(dotIndex) : '';
  return base.length > 30 ? `${base.substring(0, 30)}...${ext}` : name;
};

const BulkUpdateDefaultRatesForm: React.FC<Props> = ({
  file,
  setFile,
  isLoading,
  mode,
  noExistingData,
  onDownloadTemplate,
  isDownloadingTemplate,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const tilesDisabled = isLoading || isDownloadingTemplate;

  return (
    <DialogContent>
      <Typography variant="body2" sx={{ marginBottom: '1rem' }}>
        {DEFAULT_RATES_DESCRIPTION_TEXT[mode]}
      </Typography>

      <Alert severity="info" sx={{ marginBottom: '1rem', textAlign: 'justify' }}>
        {DEFAULT_RATES_INFO_TEXT[mode]}
      </Alert>

      {mode === 'update' && noExistingData && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          Your organization doesn't have any existing consumables yet. Use the
          "Import New Default Rates" tab to add some first.
        </Alert>
      )}

      {(mode === 'new' || !noExistingData) && (
        <Box sx={{ display: 'flex', gap: 3, borderRadius: '8px' }}>
          <ButtonBase
            sx={{ flex: 1 }}
            disabled={tilesDisabled}
            onClick={onDownloadTemplate}>
            <Box
              sx={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                height: '12.6rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <LooksOneIcon
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  color: '#737F9C',
                }}
                fontSize="medium"
              />
              <Stack alignItems="center">
                {isDownloadingTemplate ? (
                  <CircularProgress size={32} sx={{ color: '#737F9C' }} />
                ) : (
                  <DownloadIcon sx={{ color: '#737F9C' }} fontSize="large" />
                )}
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 700 }}>
                  Download template
                </Typography>
                <Chip
                  label={DEFAULT_RATES_TEMPLATE_FILENAME[mode]}
                  sx={{
                    marginTop: 1,
                    bgcolor: '#F4F5F9',
                    color: '#737F9C',
                  }}
                  icon={<DownloadIcon />}
                />
              </Stack>
            </Box>
          </ButtonBase>

          <ButtonBase component="label" disabled={tilesDisabled} sx={{ flex: 1, borderRadius: '8px' }}>
            <input
              type="file"
              accept=".xlsx"
              disabled={tilesDisabled}
              hidden
              onChange={(e) => {
                const selected = e.target.files?.[0];
                e.target.value = '';
                if (!selected) return;
                if (!selected.name.toLowerCase().endsWith('.xlsx')) {
                  enqueueSnackbar('Please select an .xlsx file', {
                    variant: 'error',
                  });
                  return;
                }
                setFile(selected);
              }}
            />
            <Box
              sx={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                height: '12.6rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <LooksTwoIcon
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  color: '#737F9C',
                }}
                fontSize="medium"
              />
              <Stack alignItems="center">
                <CloudUploadIcon sx={{ color: '#737F9C' }} fontSize="large" />
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 700 }}>
                  Upload .xlsx
                </Typography>
                <Typography variant="caption">
                  Uploaded file must follow the template.
                </Typography>
                {file && (
                  <Chip
                    label={getFileName(file.name)}
                    sx={{
                      marginTop: 1,
                      bgcolor: '#F4F5F9',
                      color: '#737F9C',
                    }}
                    icon={<UploadIcon />}
                  />
                )}
              </Stack>
            </Box>
          </ButtonBase>
        </Box>
      )}
      {isLoading && (
        <Box
          mt={2}
          display="flex"
          justifyContent="center"
          flexDirection="column">
          <Typography variant="caption" color="textSecondary">
            This process may take a while depending on the size of the file.
          </Typography>
          <LinearProgress sx={{ width: '100%', marginTop: 5 }} />
        </Box>
      )}
    </DialogContent>
  );
};

export default BulkUpdateDefaultRatesForm;
